import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import { blogService } from '../services/blogService';
import { Blog, ContentBlockType } from '../types/blog';

const BlogPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const blogData = await blogService.getBlogById(id);
        setBlog(blogData);
      } catch (err) {
        setError('Failed to load blog');
        console.error('Error fetching blog:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderContentBlock = (block: any, index: number) => {
    switch (block.type) {
      case ContentBlockType.TEXT:
        return (
          <div key={index}>
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: block.content || '' }}
            />
          </div>
        );

      case ContentBlockType.IMAGE:
        return (
          <div key={index} className="my-8">
            <img
              src={block.content}
              alt={block.alt || ''}
              className="w-full rounded-lg shadow-lg"
            />
            {block.caption && (
              <p className="text-sm text-gray-600 mt-3 text-center italic">
                {block.caption}
              </p>
            )}
          </div>
        );

      case ContentBlockType.VIDEO:
        return (
          <div key={index} className="my-8">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              {block.content.includes('youtube.com') || block.content.includes('youtu.be') ? (
                <iframe
                  src={block.content
                    .replace('watch?v=', 'embed/')
                    .replace('youtu.be/', 'youtube.com/embed/')
                    .split('&')[0]}
                  frameBorder="0"
                  allowFullScreen
                  title="YouTube video player"
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                />
              ) : block.content.includes('vimeo.com') ? (
                <iframe
                  src={block.content.replace('vimeo.com/', 'player.vimeo.com/video/')}
                  frameBorder="0"
                  allowFullScreen
                  title="Vimeo video player"
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                />
              ) : (
                <video
                  src={block.content}
                  controls
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  poster={block.videoThumbnail}
                />
              )}
            </div>
            {block.caption && (
              <p className="text-sm text-gray-600 mt-3 text-center italic">
                {block.caption}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (error || !blog) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Blog Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/blogs')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <ArrowLeftIcon className="-ml-1 mr-2 h-4 w-4" />
              Back to Blogs
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <button
            onClick={() => navigate('/blogs')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="-ml-1 mr-1 h-4 w-4" />
            Back to Blogs
          </button>
          <button
            onClick={() => navigate(`/blogs/${blog._id}/edit`)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
            Edit
          </button>
        </div>

        {/* Blog Content */}
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Featured Image */}
          {blog.featuredImage && (
            <div className="w-full h-64 sm:h-80 lg:h-96">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 sm:p-8 lg:p-12">
            {/* Title and Meta */}
            <header className="mb-8">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {blog.title}
              </h1>

              {blog.excerpt && (
                <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
                  {blog.excerpt}
                </p>
              )}

              <div className="flex items-center text-sm text-gray-500 space-x-4">
                <span>
                  By {blog.author.firstName} {blog.author.lastName}
                </span>
                <span>•</span>
                <span>
                  {blog.publishedAt ? formatDate(blog.publishedAt) : formatDate(blog.createdAt)}
                </span>
                <span>•</span>
                <span>{blog.views} views</span>
              </div>

              {/* Status Badge */}
              <div className="mt-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${blog.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : blog.status === 'draft'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                  {blog.status.charAt(0).toUpperCase() + blog.status.slice(1)}
                </span>
                {blog.priority > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Priority: {blog.priority}
                  </span>
                )}
              </div>
            </header>

            {/* Content Blocks */}
            <div className="space-y-8">
              {blog.contentBlocks.map((block, index) => renderContentBlock(block, index))}
            </div>

            {/* Tags */}
            {blog.tags.length > 0 && (
              <footer className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </footer>
            )}
          </div>
        </article>
      </div>
    </Layout>
  );
};

export default BlogPreview; 